import React from "react";
import * as Sentry from "@sentry/react";
import { Routes, Route, Link, useNavigate } from "react-router-dom";
import KitchenDashboard from "./pages/KitchenDashboard";
import FrontdeskDashboard from "./pages/FrontdeskDashboard";
import SupportDashboard from "./pages/SupportDashboard";
import AdminDashboard from "./pages/AdminDashboard";
import Login from "./pages/Login";
import ProtectedRoute from "./components/ProtectedRoute";
import { getUser, logout } from "./services/authApi";
import { RestaurantProvider, useRestaurant } from "./contexts/RestaurantContext";

const btnStyle = {
  padding: "10px 14px",
  border: "none",
  borderRadius: "8px",
  cursor: "pointer",
};

function NavBar() {
  const user = getUser();
  const navigate = useNavigate();
  const { restaurant, reload } = useRestaurant();
  const modules = restaurant?.modules || [];
  const branches = restaurant?.branches || [];

  if (!user) return null;

  const canSeeFrontdesk = ["mostrador", "admin"].includes(user.role) && modules.includes("frontdesk");
  const canSeeKitchen = ["cocina", "admin"].includes(user.role) && modules.includes("kitchen");
  const canSeeSupport = ["soporte", "admin"].includes(user.role) && modules.includes("support");
  const canSeeAdmin = user.role === "admin" && modules.includes("admin");

  const handleLogout = () => {
    logout();
    reload();
    navigate("/login", { replace: true });
  };

  return (
    <div
      style={{
        display: "flex",
        gap: "10px",
        padding: "12px",
        background: "#111",
        alignItems: "center",
      }}
    >
      {restaurant?.logo_url ? (
        <img
          src={restaurant.logo_url}
          alt={restaurant.name}
          style={{ height: 32, borderRadius: 4 }}
        />
      ) : (
        <span style={{ color: "#fff", fontWeight: "bold", fontSize: 15 }}>
          {restaurant?.name || "🍕 Casa Pizza"}
        </span>
      )}

      {canSeeFrontdesk && (
        <Link to="/">
          <button style={btnStyle}>🍕 Frontdesk</button>
        </Link>
      )}

      {canSeeKitchen && branches.map(b => (
        <Link key={b.id} to={`/kitchen/${b.id}`}>
          <button style={btnStyle}>👨‍🍳 Cocina {b.name}</button>
        </Link>
      ))}

      {canSeeSupport && (
        <Link to="/support">
          <button style={btnStyle}>💬 Soporte</button>
        </Link>
      )}

      {canSeeAdmin && (
        <Link to="/admin">
          <button style={{ ...btnStyle, background: "var(--accent)", color: "#fff" }}>
            📊 Admin
          </button>
        </Link>
      )}

      <div
        style={{
          marginLeft: "auto",
          color: "#888",
          fontSize: "13px",
          display: "flex",
          alignItems: "center",
          gap: "12px",
        }}
      >
        <span>{user.username} · {user.role}</span>
        <button
          onClick={handleLogout}
          style={{ ...btnStyle, background: "#333", color: "#ccc" }}
        >
          Salir
        </button>
      </div>
    </div>
  );
}

function ErrorFallback() {
  return (
    <div style={{ padding: "40px", textAlign: "center", color: "#ccc" }}>
      <h2>Ocurrió un error inesperado</h2>
      <p>El equipo ya fue notificado. Intenta recargar la página.</p>
      <button onClick={() => window.location.reload()} style={btnStyle}>
        Recargar
      </button>
    </div>
  );
}

function AppShell() {
  const { restaurant, loading } = useRestaurant();

  // Sin restaurant cargado todavía puede significar dos cosas: (a) no hay
  // sesión iniciada — en ese caso se registran todas las rutas y
  // ProtectedRoute redirige a /login como siempre; (b) sí hay token pero
  // GET /restaurant/config aún no responde — ahí `loading` es true y se
  // evita registrar rutas para no bloquear un módulo real por una carrera
  // de datos. Con el restaurant ya cargado, cada ruta exige su módulo.
  const moduleAllowed = (mod) => !restaurant || restaurant.modules.includes(mod);

  if (loading) {
    return (
      <div style={{ padding: "40px", textAlign: "center", color: "#888" }}>
        Cargando…
      </div>
    );
  }

  return (
    <div className="App">
      <NavBar />
      <Routes>
        <Route path="/login" element={<Login />} />
        {moduleAllowed("frontdesk") && (
          <Route
            path="/"
            element={
              <ProtectedRoute roles={["mostrador", "admin"]} requiredModule="frontdesk">
                <FrontdeskDashboard />
              </ProtectedRoute>
            }
          />
        )}
        {moduleAllowed("kitchen") && (
          <Route
            path="/kitchen/:branchId"
            element={
              <ProtectedRoute roles={["cocina", "admin"]} requiredModule="kitchen">
                <KitchenDashboard />
              </ProtectedRoute>
            }
          />
        )}
        {moduleAllowed("support") && (
          <Route
            path="/support"
            element={
              <ProtectedRoute roles={["soporte", "admin"]} requiredModule="support">
                <SupportDashboard />
              </ProtectedRoute>
            }
          />
        )}
        {moduleAllowed("admin") && (
          <Route
            path="/admin"
            element={
              <ProtectedRoute roles={["admin"]} requiredModule="admin">
                <AdminDashboard />
              </ProtectedRoute>
            }
          />
        )}
      </Routes>
    </div>
  );
}

function App() {
  return (
    <Sentry.ErrorBoundary fallback={<ErrorFallback />}>
      <RestaurantProvider>
        <AppShell />
      </RestaurantProvider>
    </Sentry.ErrorBoundary>
  );
}

export default App;
